import React from 'react';
import { TextField, Styles, FlatButton, Paper } from 'material-ui';

import { Colors } from 'components/Material/Styles/Colors';
import FileSelectorDialog from 'components/Widgets/FileSelectorDialog';
import DropZone from 'components/DropZone';
import { FormattedMessage } from 'react-intl';
import dropZoneStyles from 'components/Page/Module/Styles';
import CircularProgress from 'components/Material/CircularProgress';
import ResponsiveImage from 'components/ResponsiveImage';
import Overlay from 'components/Overlay';
import { FlexBox } from 'components';

import * as Icons from 'components/Material/SvgIcons';
import PageModule from 'components/PageModule';
import Message from 'services/Message';
import SteelCompass from 'services/SteelCompass';

const imageScalingWidth = Math.min(2000, 0.9 * window.screen.width);

const { Spacing } = Styles;

const styles = {
  detail: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: Spacing.desktopGutterMini
  },
  header: {
    fontSize: '24px',
    margin: 0,
    fontWeight: 100
  },
  progress: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    textAlign: 'center'
  },
  scrollContainer: {
    overflow: 'auto',
    maxHeight: '95vh',
  },
  imageContainer: {
    position: 'fixed',
    left: Spacing.desktopGutter,
    right: Spacing.desktopGutter,
    top: Spacing.desktopGutter,
    bottom: Spacing.desktopGutter,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  overlayImage: {
    borderRadius: 2,
    borderColor: Colors.grey[500].material,
    borderWidth: 1,
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%'
  }
};

export default class ImageModule extends PageModule {
  static readable = 'ImageModule';

  state = {
    edit: {}
  };

  constructor() {
    super(...arguments);
    const { sources, content, params } = this.props;
    let url;
    let fileId;
    const { caption } = params;
    if (sources && sources.length && sources.length > 0) {
      url = sources[0].data;
      fileId = params && params.file_id && params.file_id[0];
    } else if (params.fileId) {
      url = params.url;
      fileId = params.fileId;
    } else if (content && content.length && content.length > 0) {
      url = content[0].url;
      fileId = content[0].fileId;
    }
    this.state = {
      edit: {
        url,
        fileId,
        caption
      }
    };
  }

  componentWillReceiveProps(nextProps) {
    const { sources, content, params } = nextProps;
    let url;
    let fileId;
    const { caption } = params;
    if (sources && sources.length && sources.length > 0) {
      url = sources[0].data;
      fileId = params && params.file_id && params.file_id[0];
    } else if (params.fileId) {
      url = params.url;
      fileId = params.fileId;
    } else if (content && content.length && content.length > 0) {
      url = content[0].url;
      fileId = content[0].fileId;
    }
    if (url && fileId) {
      this.setState({ edit: { url, fileId, caption } });
    }
  }

  onMediaLibrary() {
    this.setState({ fileSelectorOpen: true });
  }

  onMediaLibraryClose() {
    this.setState({ fileSelectorOpen: false });
  }

  openDownload(event) {
    this.refs.dropZone.open();
    event.stopPropagation();
    event.preventDefault();
  }

  selectMedia(file) {
    const edit = {
      ...this.state.edit,
      fileId: file.id,
      url: file.url
    };
    this.setState({ edit, fileSelectorOpen: false });
    this.autoSave();
  }

  import() {
    const url = this.refs.import.value.trim();
    if (url === '') {
      Message.info(this.context.getIntlMessage('image.provideImportUrl'));
      return;
    }
    SteelCompass.call('file', 'import', { url, encode: false }, response => {
      if (!response.isError) {
        const edit = {
          ...this.state.edit,
          url: response.data.file.url,
          fileId: response.data.file.id
        };
        this.setState({ edit });
        this.autoSave();
        Message.info(this.context.getIntlMessage('image.imported'));
      } else {
        Message.info(this.context.getIntlMessage('image.importFailed'));
      }
    });
  }

  progress(event) {
    this.setState({ progress: event.percent });
  }

  attach(data) {
    const edit = {
      ...this.state.edit,
      fileId: data[0] && data[0].file.id,
      url: data[0] && data[0].file.url
    };
    this.setState({ edit });
    this.autoSave();
  }

  openOverlay() {
    this.setState({ overlayOpen: true });
  }

  closeOverlay() {
    this.setState({ overlayOpen: false });
  }

  changeCaption(e) {
    const edit = {
      ...this.state.edit,
      caption: e.target.value
    };
    this.setState({ edit });
  }

  saveOnBlur() {
    Message.info(this.context.getIntlMessage('video.changesSaved'));
    this.save(false);
  }

  autoSave() {
    if (this._timer) {
      clearTimeout(this._timer);
    }
    this._timer = setTimeout(() => {
      Message.info(this.context.getIntlMessage('video.changesSaved'));
      this.save(false);
      this._timer = null;
    }, 1000);
  }

  getModuleData(content, data) {
    return {
      caption: this.state.edit.caption,
      fileId: this.state.edit.fileId || data.fileId
    };
  }

  renderEditMode() {
    const { edit } = this.state || {};
    let image;

    const uploadLink = (
      <a onTouchTap={::this.openDownload}>
        {this.context.getIntlMessage('module.upload')}
      </a>
    );
    const libraryLink = (
      <a onTouchTap={::this.onMediaLibrary}>
        {this.context.getIntlMessage('module.library')}
      </a>
    );
    if (this.state.progress > 0 && this.state.progress < 100) {
      image = (
        <CircularProgress
          progressColor={this.context.muiTheme.rawTheme.palette.primary1Color}
          progress={this.state.progress}
          size={2}
        />
      );
    } else if (edit.url) {
      const { data: { params } } = this.props;
      image = (
        <ResponsiveImage
          raw={params.hasOwnProperty('raw') && params.raw}
          style={{
            width: '100%',
            height: 300
          }}
          contain
          size={imageScalingWidth}
          src={edit.url}
        />
      );
    } else {
      image = <Icons.ImageImage style={dropZoneStyles.icon} />;
    }
    return (
      <div>
        <DropZone
          ref="dropZone"
          style={dropZoneStyles.dropZone}
          lineHeight={Spacing.desktopKeylineIncrement / 2}
          onProgress={::this.progress}
          onSuccess={::this.attach}
        >
          <div style={dropZoneStyles.moduleContainer}>{image}</div>
          <div style={dropZoneStyles.header}>
            {this.context.getIntlMessage('module.dropImage')}
          </div>
          <div style={dropZoneStyles.subHeader}>
            <FormattedMessage
              message={this.context.getIntlMessage('module.uploadOrBrowse')}
              upload={uploadLink}
              library={libraryLink}
            />
          </div>
          <div style={dropZoneStyles.subHeader}>
            {this.context.getIntlMessage('module.youCanImport')}
          </div>
          <Paper style={dropZoneStyles.import.root}>
            <FlexBox>
              <FlexBox>
                <input
                  ref={'import'}
                  style={dropZoneStyles.import.input}
                  type="text"
                  placeholder={this.context.getIntlMessage('importFromUrl')}
                  onChange={::this.import}
                />
              </FlexBox>
            </FlexBox>
          </Paper>

          <TextField
            ref="caption"
            fullWidth
            hintText={this.context.getIntlMessage('Caption')}
            value={edit.caption}
            onChange={::this.changeCaption}
            onBlur={::this.saveOnBlur}
          />
        </DropZone>
        <FileSelectorDialog
          type="image"
          open={this.state.fileSelectorOpen}
          onSelect={::this.selectMedia}
          onClose={::this.onMediaLibraryClose}
        />
      </div>
    );
  }

  renderDetailMode() {
    const { url, caption } = this.state.edit;
    const { data: { params } } = this.props;
    let image;
    if (url) {
      if (this.state.overlayOpen) {
        image = (
          <Overlay open={this.state.overlayOpen} onClose={::this.closeOverlay}>
            <div style={styles.imageContainer} onTouchTap={::this.closeOverlay}>
              <div style={styles.scrollContainer}>
                <ResponsiveImage
                  contain
                  raw={params.hasOwnProperty('raw') && params.raw}
                  size={imageScalingWidth}
                  style={styles.overlayImage}
                  src={url}
                />
              </div>
            </div>
          </Overlay>
        );
      } else {
        if (params.hasOwnProperty('raw') && params.raw) {
          image = (
            <ResponsiveImage
              onTouchTap={::this.openOverlay}
              raw={params.raw}
              src={url}
              style={{
                width: 'inherit',
                cursor: 'pointer'
              }}
            />
          );
        } else {
          image = (
            <ResponsiveImage
              contain
              size={imageScalingWidth}
              onTouchTap={::this.openOverlay}
              src={url}
              style={{
                maxWidth: '100%',
                cursor: 'pointer',
                width: '100%'
              }}
            />
          );
        }
      }
    } else {
      image = (
        <div style={dropZoneStyles.dropZone}>
          <div style={dropZoneStyles.moduleContainer}>
            <Icons.ImageImage style={dropZoneStyles.icon} />
            <br />
            {this.context.getIntlMessage('module.uploadImage')}
          </div>
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'center', marginBottom: 42 }}>
        {image}
        <figcaption style={{ fontSize: '13px', fontStyle: 'italic' }}>
          {caption}
        </figcaption>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/js/components/Page/Module/Image.js